import type { DoctorAssessment, DoctorReport } from "../surfaces/registry.js";
import type { FirstValueInitiativeResult, OrgxTask } from "./initiatives.js";
import type { SetupVerificationSummary } from "./setup-verification.js";
import type { WorkspaceSetupResult } from "./setup-workspace.js";
import type { WizardAgentRosterRecord } from "./wizard-state.js";

function withBaseProperties(
  properties: Record<string, unknown>,
  base: Record<string, unknown>,
): Record<string, unknown> {
  return {
    ...base,
    ...properties,
  };
}

export function buildWorkspaceSetupTelemetryProperties(
  result: WorkspaceSetupResult,
  base: Record<string, unknown> = {},
): Record<string, unknown> {
  return withBaseProperties(
    {
      created: result.created === true,
      default_changed: result.defaultChanged === true,
      has_workspace: Boolean(result.workspace),
      status: result.status,
    },
    base,
  );
}

export function buildOnboardingTaskTelemetryProperties(
  input: {
    created: boolean;
    hasInitiative: boolean;
    task: Pick<OrgxTask, "status">;
  },
  base: Record<string, unknown> = {},
): Record<string, unknown> {
  return withBaseProperties(
    {
      created: input.created,
      has_initiative: input.hasInitiative,
      task_status: input.task.status ?? "unknown",
    },
    base,
  );
}

export function buildAgentRosterTelemetryProperties(
  input: {
    roster: WizardAgentRosterRecord;
    strategy: "default" | "guided";
  },
  base: Record<string, unknown> = {},
): Record<string, unknown> {
  return withBaseProperties(
    {
      agent_count: input.roster.agents.length,
      domain_count: new Set(input.roster.agents.map((agent) => agent.domain)).size,
      strategy: input.strategy,
    },
    base,
  );
}

export function buildFirstValueInitiativeTelemetryProperties(
  result: FirstValueInitiativeResult,
  base: Record<string, unknown> = {},
): Record<string, unknown> {
  return withBaseProperties(
    {
      created: result.created,
      has_live_url: Boolean(result.liveUrl),
    },
    base,
  );
}

export function buildDoctorTelemetryProperties(
  report: DoctorReport,
  assessment: DoctorAssessment,
  verification: SetupVerificationSummary,
  base: Record<string, unknown> = {},
): Record<string, unknown> {
  const errorCount = assessment.issues.filter((issue) => issue.level === "error").length;
  const warningCount = assessment.issues.filter((issue) => issue.level === "warning").length;

  return withBaseProperties(
    {
      auth_configured: report.auth.configured,
      auth_ok: report.auth.ok,
      configured_surface_count: report.surfaces.filter((surface) => surface.configured).length,
      detected_surface_count: report.surfaces.filter((surface) => surface.detected).length,
      error_count: errorCount,
      hosted_mcp_degraded: verification.hostedMcpDegraded,
      hosted_mcp_ok: report.hostedMcp.ok,
      hosted_mcp_tool_ok: report.hostedMcpTool.ok,
      transient_timeouts: verification.transientTimeouts,
      issue_count: assessment.issues.length,
      openclaw_available: !report.openclaw.skipped,
      openclaw_ok: report.openclaw.ok,
      status: verification.status,
      warning_count: warningCount,
      workspace_configured: report.workspace.configured,
      workspace_ok: report.workspace.ok,
    },
    base,
  );
}
